import { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import { Cloud, CloudRain, CloudSnow, CloudFog, CloudLightning, Sun, CloudSun, Moon, Wind, Droplets, RefreshCw, MapPin } from "lucide-react";

/** Pick a lucide-react icon for a MET.no symbol_code. */
function symbolIcon(symbol = "") {
  const s = symbol.toLowerCase();
  if (s.includes("thunder") || s.includes("lightning")) return CloudLightning;
  if (s.includes("snow") || s.includes("sleet")) return CloudSnow;
  if (s.includes("rain") || s.includes("showers")) return CloudRain;
  if (s.includes("fog")) return CloudFog;
  if (s.includes("cloudy") && !s.includes("partly") && !s.includes("fair")) return Cloud;
  if (s.includes("partly") || s.includes("fair")) {
    return s.includes("night") ? Moon : CloudSun;
  }
  if (s.includes("clearsky")) {
    return s.includes("night") ? Moon : Sun;
  }
  return Cloud;
}

function formatHour(iso) {
  try {
    const d = new Date(iso);
    return `${String(d.getHours()).padStart(2, "0")}:00`;
  } catch { return "--:--"; }
}

/** Compact weather card for the top of Home / Weekly dashboards.
 *  Priority for location:
 *    1. User's current GPS (with 2 sec timeout, one-shot)
 *    2. Last known GPS cached in localStorage (key: pb_last_gps, 24 h TTL)
 *    3. `place` prop (e.g. household.address)
 *    4. Backend default (Oslo)
 *  - `accent`: tailwind color token, e.g. "amber" | "navy" | "emerald".
 */
export default function WeatherWidget({ place, accent = "navy" }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [geoLabel, setGeoLabel] = useState(""); // "GPS" | "sist GPS" | "adresse" | ""
  const loadedOnce = useRef(false);

  /** Fetch weather either by coords or place, updating state. */
  const fetchWeather = async ({ lat, lon, place: placeArg, sourceLabel }) => {
    setErr("");
    try {
      const params = lat != null && lon != null ? { lat, lon } : placeArg ? { place: placeArg } : {};
      const res = await api.getWeather(params);
      setData(res.data);
      setGeoLabel(sourceLabel || "");
    } catch (e) {
      setErr(e.response?.data?.detail || "Kunne ikke hente værdata");
    } finally {
      setLoading(false);
    }
  };

  /** Ask the browser for current GPS. Resolves to {lat, lon} or null. */
  const tryGeolocation = (timeoutMs = 8000, maxAge = 60000) => new Promise((resolve) => {
    if (!("geolocation" in navigator)) return resolve(null);
    let settled = false;
    const timer = setTimeout(() => { if (!settled) { settled = true; resolve(null); } }, timeoutMs);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        resolve({
          lat: +pos.coords.latitude.toFixed(4),
          lon: +pos.coords.longitude.toFixed(4),
        });
      },
      () => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        resolve(null);
      },
      // High accuracy for boats — they move > 500m frequently. Accept cache up
      // to 1 min old (was 10 min — too stale for a vessel underway).
      { enableHighAccuracy: true, timeout: timeoutMs, maximumAge: maxAge },
    );
  });

  const load = async (opts = {}) => {
    const forceFresh = !!opts.forceFresh;
    setLoading(true);
    setErr("");
    // Step 1: try cached GPS (fast, no prompt) — but only if not forcing fresh.
    // TTL reduced from 24h to 1h: a boat moves enough that yesterday's GPS
    // is misleading. The cache exists only to render something during the
    // 2-8 sec it takes the browser to return live GPS.
    let cached = null;
    if (!forceFresh) {
      try {
        const raw = localStorage.getItem("pb_last_gps");
        if (raw) {
          const parsed = JSON.parse(raw);
          if (parsed && parsed.lat != null && parsed.lon != null && (Date.now() - (parsed.ts || 0) < 60 * 60 * 1000)) {
            cached = parsed;
          }
        }
      } catch { /* ignore */ }
    }

    if (cached) {
      await fetchWeather({ lat: cached.lat, lon: cached.lon, sourceLabel: "sist GPS" });
    } else if (place && !forceFresh) {
      await fetchWeather({ place, sourceLabel: "adresse" });
    }

    // Step 2: ALWAYS try to refresh with live GPS. On forceFresh, ask the
    // browser for a brand-new fix (maxAge=0) so manual refresh genuinely
    // re-locates the user (matters for a vessel underway).
    const fresh = await tryGeolocation(8000, forceFresh ? 0 : 60000);
    if (fresh) {
      localStorage.setItem("pb_last_gps", JSON.stringify({ ...fresh, ts: Date.now() }));
      const moved = !cached || Math.abs(cached.lat - fresh.lat) > 0.005 || Math.abs(cached.lon - fresh.lon) > 0.005;
      if (moved || forceFresh) {
        await fetchWeather({ lat: fresh.lat, lon: fresh.lon, sourceLabel: "GPS" });
      } else if (geoLabel !== "GPS") {
        setGeoLabel("GPS");
      }
    } else if (!cached && !place) {
      // No GPS available + no cache + no place → fall back to backend default
      await fetchWeather({ sourceLabel: "" });
    }
    setLoading(false);
  };

  useEffect(() => {
    if (loadedOnce.current) return;
    loadedOnce.current = true;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const ringColor = {
    amber: "border-amber-100 bg-amber-50/40",
    navy: "border-navy-100 bg-sailcloth-100/40",
    emerald: "border-emerald-100 bg-emerald-50/40",
  }[accent] || "border-stone-200 bg-white";

  const iconColor = {
    amber: "text-amber-700",
    navy: "text-navy-800",
    emerald: "text-emerald-700",
  }[accent] || "text-stone-700";

  if (loading) {
    return (
      <div className={`rounded-xl border ${ringColor} p-4 flex items-center gap-3 text-sm text-stone-500`} data-testid="weather-widget-loading">
        <RefreshCw className="h-4 w-4 animate-spin" /> Laster vær...
      </div>
    );
  }
  if (err || !data?.now) {
    return (
      <div className={`rounded-xl border ${ringColor} p-4 flex items-center justify-between text-sm text-stone-500`} data-testid="weather-widget-error">
        <span>{err || "Ingen vær-data"}</span>
        <button onClick={() => load({ forceFresh: true })} className="inline-flex items-center gap-1 text-xs font-semibold hover:underline" data-testid="weather-retry-btn">
          <RefreshCw className="h-3 w-3" /> Prøv igjen
        </button>
      </div>
    );
  }

  const Icon = symbolIcon(data.now.symbol);
  const hours = (data.hourly || []).slice(0, 8);

  return (
    <div className={`rounded-xl border ${ringColor} p-4 sm:p-5`} data-testid="weather-widget">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <Icon className={`h-10 w-10 ${iconColor}`} />
          <div>
            <div className="flex items-baseline gap-2">
              <span className="text-3xl font-bold text-stone-900" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                {Math.round(data.now.temp)}°
              </span>
              <span className="text-xs text-stone-500 uppercase tracking-wider">{data.place || (geoLabel ? "Din posisjon" : "Vær")}</span>
              {geoLabel && (
                <span className={`inline-flex items-center gap-0.5 text-[9px] font-semibold uppercase tracking-wider rounded px-1.5 py-0.5 ${geoLabel === "GPS" ? "bg-emerald-100 text-emerald-700" : "bg-stone-100 text-stone-500"}`} title={geoLabel === "GPS" ? "Live GPS-posisjon" : geoLabel === "sist GPS" ? "Sist kjente GPS-posisjon" : "Basert på adresse"} data-testid="weather-source-label">
                  <MapPin className="h-2.5 w-2.5" /> {geoLabel}
                </span>
              )}
            </div>
            <div className="text-sm text-stone-600">{data.now.label}</div>
            <div className="flex items-center gap-3 mt-1 text-xs text-stone-500">
              <span className="inline-flex items-center gap-1"><Wind className="h-3 w-3" /> {data.now.wind} m/s</span>
              <span className="inline-flex items-center gap-1"><Droplets className="h-3 w-3" /> {data.now.humidity}%</span>
              {data.day && (
                <span className="hidden sm:inline text-stone-400">
                  {Math.round(data.day.min)}° / {Math.round(data.day.max)}°
                </span>
              )}
            </div>
          </div>
        </div>
        <button
          onClick={() => load({ forceFresh: true })}
          className="p-1.5 rounded-md text-stone-400 hover:bg-stone-100 hover:text-stone-600"
          title="Oppdater (fersk GPS)"
          data-testid="weather-refresh-btn"
        >
          <RefreshCw className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* 8-hour strip */}
      <div className="mt-3 grid grid-cols-8 gap-1 sm:gap-2" data-testid="weather-hourly">
        {hours.map((h, idx) => {
          const HIcon = symbolIcon(h.symbol);
          return (
            <div key={h.time || `h-${idx}`} className="flex flex-col items-center text-center py-1">
              <span className="text-[10px] text-stone-400">{formatHour(h.time)}</span>
              <HIcon className={`h-4 w-4 my-0.5 ${iconColor}`} />
              <span className="text-[11px] font-semibold text-stone-700">{Math.round(h.temp)}°</span>
              {h.precip_mm > 0 && (
                <span className="text-[9px] text-blue-600">{h.precip_mm}mm</span>
              )}
            </div>
          );
        })}
      </div>

      {/* Dagens tips (rule-based one-liner from the backend) */}
      {data.tip && (
        <div className="mt-3 rounded-lg bg-white/60 border border-stone-100 px-3 py-2 text-xs text-stone-700" data-testid="weather-tip">
          <span className="font-semibold text-stone-500 uppercase tracking-wider text-[9px] mr-1">Tips</span>
          {data.tip}
        </div>
      )}

      <div className="mt-2 flex items-center justify-between text-[10px] text-stone-400">
        <span>Kilde: MET.no / Yr.no</span>
        {data.cached && <span>Mellomlagret</span>}
      </div>
    </div>
  );
}
